import React from "react";
import { StoreSettings } from "../../context/StoreSettingsContext";

interface MinimumOrderCardProps {
  storeSettings: StoreSettings | null;
  t: (key: string, options?: any) => string;
}

const MinimumOrderCard: React.FC<MinimumOrderCardProps> = ({ storeSettings, t }) => {
  if (!storeSettings) return null;

  const minimumOrder = storeSettings.minimumOrder || 0;
  const pendingOrders = storeSettings.pendingOrders || "0";

  // Convert to number for comparison
  const pendingOrdersNum = parseInt(pendingOrders, 10);

  // Get order type text
  const getOrderTypeText = () => {
    if (storeSettings.deliveryEnabled && storeSettings.pickupEnabled) {
      return t("MinimumOrderCard.orderTypes.both");
    } else if (storeSettings.deliveryEnabled) {
      return t("MinimumOrderCard.orderTypes.delivery");
    } else if (storeSettings.pickupEnabled) {
      return t("MinimumOrderCard.orderTypes.pickup");
    }
    return t("MinimumOrderCard.orderTypes.none");
  };

  // Get status text based on pending orders
  const getStatusText = () => {
    if (pendingOrdersNum === 0) {
      return t("MinimumOrderCard.orderStatus.allClear");
    } else if (pendingOrdersNum === 1) {
      return t("MinimumOrderCard.orderStatus.needsAttention_one", { count: pendingOrdersNum });
    } else {
      return t("MinimumOrderCard.orderStatus.needsAttention_other", { count: pendingOrdersNum });
    }
  };

  // Get status class based on pending orders
  const getStatusClass = () => {
    if (pendingOrdersNum === 0) {
      return "bg-green-100 text-green-800";
    } else if (pendingOrdersNum < 5) {
      return "bg-yellow-100 text-yellow-800";
    } else {
      return "bg-red-100 text-red-800";
    }
  };

  // Get order label (singular/plural)
  const getOrderLabel = () => {
    if (pendingOrdersNum === 1) {
      return t("MinimumOrderCard.pendingOrders_one", { count: 1 });
    }
    return t("MinimumOrderCard.pendingOrders_other", { count: pendingOrdersNum });
  };

  return (
    <div className="space-y-4">
      {/* Pending Orders Card */}
      <div className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl shadow-sm hover:shadow-md transition-shadow duration-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <div className="p-2 bg-gradient-to-r from-blue-100 to-indigo-100 rounded-lg mr-3 shadow-sm">
              <svg 
                className="w-5 h-5 text-blue-600" 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth="2" 
                  d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                />
              </svg>
            </div>
            <div>
              <p className="text-sm font-medium text-blue-800">
                {t("MinimumOrderCard.pendingOrdersLabel")}
              </p>
              <div className="flex items-baseline">
                <p className="text-2xl font-bold text-blue-900">
                  {pendingOrders}
                </p>
                <span className="ml-2 text-sm text-blue-600">
                  {getOrderLabel()}
                </span>
              </div>
            </div>
          </div>
          
          {/* Status indicator */}
          <div className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusClass()}`}>
            {getStatusText()}
          </div>
        </div>
        
        {/* Progress indicator for multiple orders */}
        {pendingOrdersNum > 0 && (
          <div className="mt-3">
            <div className="flex justify-between text-xs text-blue-600 mb-1">
              <span>{t("MinimumOrderCard.progress.queue")}</span>
              <span>{pendingOrdersNum}/10</span>
            </div>
            <div className="w-full bg-blue-200 rounded-full h-2">
              <div 
                className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                style={{ 
                  width: `${Math.min(pendingOrdersNum * 10, 100)}%` 
                }}
              ></div>
            </div>
          </div>
        )}
      </div>

      {/* Minimum Order Card */}
      {minimumOrder > 0 ? (
        <div className="p-4 bg-gradient-to-r from-yellow-50 to-amber-50 border border-yellow-200 rounded-xl shadow-sm hover:shadow-md transition-shadow duration-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="p-2 bg-gradient-to-r from-yellow-100 to-amber-100 rounded-lg mr-3 shadow-sm">
                <span className="text-yellow-800 font-bold text-xl">
                  {t("MinimumOrderCard.currency.symbol")}
                </span>
              </div>
              <div>
                <p className="text-sm font-medium text-yellow-800">
                  {t("MinimumOrderCard.minimumOrder")}
                </p>
                <p className="text-2xl font-bold text-yellow-900">
                  {t("MinimumOrderCard.currency.format", {
                    value: minimumOrder.toFixed(2),
                    symbol: t("MinimumOrderCard.currency.symbol")
                  })}
                </p>
              </div>
            </div>
            
            {/* Info icon with tooltip */}
            <button 
              className="p-1 text-yellow-500 hover:text-yellow-600 transition-colors"
              aria-label={t("MinimumOrderCard.tooltips.minimumOrder")}
              title={t("MinimumOrderCard.tooltips.minimumOrder")}
            >
              <svg 
                className="w-5 h-5" 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth="2" 
                  d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </button>
          </div>
          
          {/* Additional context */}
          <div className="mt-3 pt-3 border-t border-yellow-200">
            <p className="text-xs text-yellow-700">
              {t("MinimumOrderCard.appliesTo")} {getOrderTypeText()}
            </p>
          </div>
        </div>
      ) : (
        <div className="p-4 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl shadow-sm">
          <div className="flex items-center">
            <div className="p-2 bg-gradient-to-r from-green-100 to-emerald-100 rounded-lg mr-3 shadow-sm">
              <svg 
                className="w-5 h-5 text-green-600" 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth="2" 
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
            <div>
              <p className="text-sm font-medium text-green-800">
                {t("MinimumOrderCard.minimumOrder")}
              </p>
              <p className="text-lg font-bold text-green-900">
                {t("MinimumOrderCard.alerts.noMinimum")}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MinimumOrderCard;